import { HttpClient, HttpHeaders } from '@angular/common/http';
import { computed, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { EnvironmentService } from '../../services/environment/environment.service';
import { Observable } from 'rxjs';
import { catchError, map, retry } from 'rxjs/operators';
import { BasicModel } from '../../models/basic/basic';
import { EndpointProvider } from './endpoint.service';
import { ErrorProvider } from './error.service';

const BASIC_REMOVE_PARAMS = ['clientId'];

@Injectable({
  providedIn: 'root'
})
export class DataProvider<Type extends BasicModel> extends EndpointProvider {
  // Properties & Signals
  /** Params which should be removed from the payload before sending to the backend */
  public removeParams: string[] = [];
  /** Prefix for the service */
  public prefix = '';
  /** Base URL for the service */
  public baseUrl = '';
  /** Channel for the service */
  protected channelId = computed(() => {
    const env = this.environment.env;
    const parsedChannel = parseInt(env.CHANNEL_ID, 10);
    if (!Number.isFinite(parsedChannel) || parsedChannel <= 0) return undefined;
    return parsedChannel;
  });
  /** Hash including the channelId if available */
  protected channelHeader = computed<{} | { channel: string }>(() => {
    const channel = this.channelId();
    return channel !== undefined ? { channel: String(channel) } : {};
  });

  // Services
  protected readonly environment = inject(EnvironmentService);
  protected readonly errorService = inject(ErrorProvider);
  protected readonly http = inject(HttpClient);
  protected readonly router = inject(Router);

  // Getters & Setters
  /** HTTP Options without Content-Type */
  get httpOptions(): { headers: HttpHeaders } {
    return {
      headers: new HttpHeaders({
        'api-version': '2.0',
        ...this.channelHeader()
      })
    };
  }
  /** HTTP Options with Content-Type */
  get httpOptionsWithContent(): any {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'api-version': '2.0',
        ...this.channelHeader()
      })
    };
  }

  // Methods
  /**
   * Generates headers for the request
   * @param additionalHeaders to append to the headers
   * @param contentJson if the resulting content is JSON
   * @returns the headers
   */
  generateHeaders(additionalHeaders: any = {}, contentJson?: boolean): HttpHeaders {
    const processedHeaders: any = { ...additionalHeaders };
    Object.keys(processedHeaders).forEach((key) => {
      if (!processedHeaders[key]) delete processedHeaders[key];
      else if (typeof processedHeaders[key] === 'object') processedHeaders[key] = JSON.stringify(processedHeaders[key]);
      else if (typeof processedHeaders[key] !== 'string') processedHeaders[key] = String(processedHeaders[key]);
    });
    return new HttpHeaders({
      'api-version': '2.0',
      ...this.channelHeader(),
      ...processedHeaders,
      ...(contentJson ? { 'Content-Type': 'application/json' } : {})
    });
  }

  /**
   * Entfernt Parameter welche nicht gesendet werden sollen
   * @param hash to remove params of
   */
  removeNotToUseParams(hash: any) {
    // Neues Objekt erstellen
    if (hash === undefined || hash == null) return;
    this.removeParams.concat(BASIC_REMOVE_PARAMS).forEach((p) => {
      delete hash[p];
    });
    Object.keys(hash).forEach((element) => {
      if (typeof hash[element] === 'object') this.removeNotToUseParams(hash[element]);
    });
  }

  /**
   * Erhält alle Objecte vom Endpunkt.
   */
  all(): Observable<Type[]> {
    return this.http
      .get<Type[]>(new URL(this.endpointWithUrl).toString(), this.httpOptions)
      .pipe(
        map((data) => {
          return DataProvider.mapCasesReverse(data);
        })
      )
      .pipe(retry(1), catchError(this.errorService.convert));
  }

  /**
   * Ruft ein Objekt mit Details ab
   * @param id
   */
  find(id: number): Observable<Type> {
    return this.http
      .get<Type>(new URL(this.endpointWithUrl + id).toString(), this.httpOptions)
      .pipe(
        map((data) => {
          return DataProvider.mapCasesReverse(data);
        })
      )
      .pipe(retry(1), catchError(this.errorService.convert));
  }

  /**
   * Sucht Objekte mit Argumenten
   * @param args
   */
  where(args: any): Observable<Type[]> {
    return this.http
      .get<Type[]>(new URL(this.endpointWithUrl + '?' + this.paramsToURI(args)).toString(), this.httpOptions)
      .pipe(
        map((data) => {
          return DataProvider.mapCasesReverse(data);
        })
      )
      .pipe(retry(1), catchError(this.errorService.convert));
  }

  /**
   * Sucht Objekte mit Argumenten
   * @param args
   */
  whereOne(args: any): Observable<Type> {
    return this.http
      .get<Type>(new URL(this.endpointWithUrl + '?' + this.paramsToURI(args)).toString(), this.httpOptions)
      .pipe(
        map((data) => {
          return DataProvider.mapCasesReverse(data);
        })
      )
      .pipe(retry(1), catchError(this.errorService.convert));
  }

  /**
   * Erzeugt/Speichert ein Objekt im Backend (je nachdem ob ID gesetzt)
   * @param object Zu speicherndes/erzeugendes Objekt
   */
  save<Payload extends BasicModel = Type, Response = Type>(object: Payload): Observable<Response> {
    if (object.id && object.id >= 0) {
      return this.update<Payload, Response>(object);
    } else {
      return this.create<Payload, Response>(object);
    }
  }

  /**
   * Erzeugt ein neues Objekt im Backend
   * @param object
   */
  private create<Payload extends BasicModel = Type, Response = Type>(object: Payload): Observable<Response> {
    delete object.id;
    return this.http
      .post<Type>(this.endpointWithUrl, this.formatPayload(object), this.httpOptionsWithContent)
      .pipe(
        map((data) => {
          return DataProvider.mapCasesReverse(data);
        })
      )
      .pipe(catchError(this.errorService.convert));
  }

  /**
   * Aktualisiert ein Objekt
   * @param id
   * @param object
   */
  private update<Payload extends BasicModel = Type, Response = Type>(object: Payload): Observable<Response> {
    return this.http
      .put<Type>(this.endpointWithUrl + object.id, this.formatPayload(object), this.httpOptionsWithContent)
      .pipe(
        map((data) => {
          return DataProvider.mapCasesReverse(data);
        })
      )
      .pipe(catchError(this.errorService.convert));
  }

  /**
   * Löscht ein Objekt im Backend
   * @param id
   */
  delete(id: number): Observable<Type> {
    return this.http
      .delete<Type>(this.endpointWithUrl + id, this.httpOptions)
      .pipe(
        map((data) => {
          return DataProvider.mapCasesReverse(data);
        })
      )
      .pipe(retry(1), catchError(this.errorService.convert));
  }

  formatPayload(obj: unknown, skipMapping?: boolean): string {
    const hashToSend: unknown = this.copy(obj);
    this.removeNotToUseParams(hashToSend);
    if (skipMapping) return JSON.stringify(hashToSend);
    else return JSON.stringify(DataProvider.mapCases(hashToSend));
  }

  paramsToURI(params: any): any {
    const urlParams = new URLSearchParams(params).toString();
    return urlParams;
  }

  copy(aObject: any) {
    if (!aObject) return aObject;
    const bObject: any = Array.isArray(aObject) ? [] : {};
    let value;
    for (const key in aObject) {
      // Prevent self-references to parent object
      if (Object.is(aObject[key], aObject)) continue;
      value = aObject[key];
      bObject[key] = typeof value === 'object' ? this.copy(value) : value;
    }
    return bObject;
  }

  /**
   * Maps from lowerCapitalCase to CapitalCase
   * @param hash or key to map
   * @returns new object with CapitalCase keys
   */
  public static mapCases(hash: any): any {
    const convertKey = (key: string): string => {
      return key.charAt(0).toUpperCase() + key.slice(1);
    };
    if (typeof hash === 'string') return convertKey(hash);
    return this.mapRecursive(hash, convertKey);
  }

  /**
   * Maps from CapitalCase to lowerCapitalCase
   * @param hash to map
   * @returns new object with lowerCapitalCase keys
   */
  public static mapCasesReverse(hash: any): any {
    const convertKey = (key: string): string => {
      return key.charAt(0).toLowerCase() + key.slice(1);
    };
    if (typeof hash === 'string') return convertKey(hash);
    return this.mapRecursive(hash, convertKey);
  }

  /**
   * Recursively maps the keys of an object
   * @param obj to map the keys of
   * @param convertKeyFunc function to convert the keys
   * @returns new object with the keys converted
   */
  private static mapRecursive(obj: any, convertKeyFunc: (key: string) => string): any {
    if (!obj || typeof obj !== 'object') {
      return obj; // Return the value if obj is not an object
    }
    if (Array.isArray(obj)) {
      return obj.map((o) => this.mapRecursive(o, convertKeyFunc)); // Recursively map arrays
    }
    const newObj: any = {};
    Object.keys(obj).forEach((key) => {
      const newKey = convertKeyFunc(key);
      newObj[newKey] = this.mapRecursive(obj[key], convertKeyFunc);
    });
    return newObj;
  }

  /**
   * Get the current locale from the url
   * @returns The locale from the url
   */
  protected get locale(): string {
    // Get the locale from the url "xxx.com/en-US/home" -> "en-US"
    const localeUrl = this.router.url.split('/')[1];
    // Check if locale is valid xx-XX
    return RegExp(/^[a-z]{2}-[A-Z]{2}$/).exec(localeUrl) ? localeUrl : 'en-US';
  }
}
